import DashboardLayout from "../../layouts/DashboardLayout";
import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { FaSpinner, FaEyeSlash, FaEye } from "react-icons/fa6";
import SettingsSkeletonLoader from "../../components/Loaders/SettingsSkeletonLoader";
import { useAppSelector, useAppDispatch } from '../../hooks'
import AppNotifications from '../../components/AppNotifications';
import { setError, setFailed, setSuccess } from "../../state/xhr-status/reqStatus";
import { Icon } from "@iconify/react";

interface FormErrors {
    name?: string;
    email?: string;
    newPwd?: string;
    newPwdDetails?: string;
    oldPwd?: string;
    oldPwdDetails?: string;
}

function DashboardSettings() {
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const session = sessionStorage.getItem("tiller") || localStorage.getItem("tiller");
    const access_token = session ? JSON.parse(session).access_token : null;
    const [loading, setLoading] = useState(false);
    const [msLoading, setMsLoading] = useState(false)
    const [zmLoading, setZmLoading] = useState(false)
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdateName, setIsUpdateName] = useState(false);
    const [isUpdateEmail, setIsUpdateEmail] = useState(false);
    const [isUpdatePassword, setIsUpdatePassword] = useState(false);
    const [oldPwdVisible, setOldPwdVisible] = useState(false);
    const [newPwdVisible, setNewPwdVisible] = useState(false);
    const [isGoogleConnected, setIsGoogleConnected] = useState(false);
    const [isMicrosoftConnected, setIsMicrosoftConnected] = useState(false);
    const [isZoomConnected, setIsZoomConnected] = useState(false)
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [lastUpdate, setLastUpdate] = useState('');
    const [oldPwd, setOldPwd] = useState('');
    const [newPwd, setNewPwd] = useState('');
    const [errors, setErrors] = useState<FormErrors>({});

    const reqStatus = useAppSelector((state) => state.reqStatus.value);
    const dispatch = useAppDispatch()
    function handleEmailChange(e: any) {
        setEmail(e.target.value);
    }

    function handleNameChange(e: any) {
        setName(e.target.value);
    }

    function handleOldPwdChange(e: any) {
        setOldPwd(e.target.value);
    }
    function handleNewPwdChange(e: any) {
        setNewPwd(e.target.value);
    }

    // Password Validation Function
    function passwordCheck(password: string) {
        const lengthError = password.length < 8;
        const digitError = !/\d/.test(password);
        const uppercaseError = !/[A-Z]/.test(password);
        const lowercaseError = !/[a-z]/.test(password);
        const symbolError = !/\W/.test(password);

        return {
            passwordOk: !(lengthError || digitError || uppercaseError || lowercaseError || symbolError),
        };
    }
    // Validate form (Name and Email)
    function validateForm(): boolean {
        let tempErrors: FormErrors = {};

        if (!name.trim()) {
            tempErrors.name = 'Name is required.';
        }
        if (!email) {
            tempErrors.email = 'Email is required.';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            tempErrors.email = 'Email is invalid.';
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    }

    // Validate Passwords Independently
    function validatePwd(pwd: string, field: 'oldPwd' | 'newPwd') {
        let tempErrors: FormErrors = { ...errors };
        const passwordErrors = passwordCheck(pwd);
        if (!passwordErrors.passwordOk) {
            tempErrors[field] = 'Password must be at least 8 characters, include an uppercase letter, a lowercase letter, a number, and a symbol.';
        } else {
            delete tempErrors[field];
        }
        setErrors(tempErrors);
    }


    const handleUpdate = async (field: string, value: string) => {
        let tempErrors: FormErrors = {};

        // Perform validation before making the update
        if (field === 'name') {
            if (!value.trim()) {
                tempErrors.name = 'Name is required.';
            }
        } else if (field === 'email') {
            if (!value.trim()) {
                tempErrors.email = 'Email is required.';
            } else if (!/\S+@\S+\.\S+/.test(value)) {
                tempErrors.email = 'Email is invalid.';
            }
        }

        // If there are validation errors, set errors and return early
        if (Object.keys(tempErrors).length > 0) {
            setErrors(tempErrors);
            return;
        }

        // Clear any existing errors for this field
        setErrors({ ...errors, [field]: undefined });
        const response = await fetch(`${apiUrl}/user`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${access_token}`,
            },
            body: JSON.stringify({ field, value }),
        });
        const data = await response.json();
        if (response.ok && field === 'email') {
            setEmail(value);
            dispatch(setSuccess({ msg: `Email updated successfully`, statusCode: 200 }));
            setIsUpdateEmail(false);
        } else if (response.ok && field === 'name') {
            setName(value);
            dispatch(setSuccess({ msg: `Name updated successfully`, statusCode: 200 }));
            setIsUpdateName(false);
        } else {
            dispatch(setError({ msg: data.message || 'Failed to update.', statusCode: 400 }));
        }
    };

    // Handle password update
    const handlePasswordUpdate = async () => {
        validatePwd(oldPwd, 'oldPwd');
        validatePwd(newPwd, 'newPwd');

        if (!oldPwd || !newPwd) {
            dispatch(setError({ msg: 'Both current and new passwords are required.', statusCode: 400 }));
            return;
        }

        if (oldPwd === newPwd) {
            dispatch(setError({ msg: "Please choose a new password that's different from your current one.", statusCode: 400 }));
            return;
        }

        try {
            const response = await fetch(`${apiUrl}/update-password`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${access_token}`,
                },
                body: JSON.stringify({
                    old_password: oldPwd,
                    new_password: newPwd,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                dispatch(setSuccess({ msg: 'Password updated successfully', statusCode: 200 }));
                setOldPwd('');
                setNewPwd('');
                setIsUpdatePassword(false);
            } else {
                dispatch(setError({ msg: data.message || 'Failed to change password.', statusCode: 400 }));
            }
        } catch (error: any) {
            dispatch(setError({ msg: `Error: ${error.message} || 'An unknown error occurred'`, statusCode: 500 }));
        }
    };

    async function handleConnectZoomClient() {
        setZmLoading(true);
        // await checkPopupBlocker()
        try {

            const response = await fetch(`${apiUrl}/zoom/authorize/`,
                {
                    method: "GET",
                    headers: {
                        "Content-type": "application/json",
                        Authorization: `Bearer ${access_token}`
                    }
                }
            );
            const data = await response.json()
            window.location.href = data.url;

        } catch (error) {
            dispatch(
                setError({
                    msg: `Failed to fetch authorization URL: ${error}`,
                    statusCode: 500,
                })
            );
        }

    };

    async function handleConnectGoogleCalendar() {
        setLoading(true);
        // await checkPopupBlocker()
        try {

            const response = await fetch(`${apiUrl}/google/authorize/`,
                {
                    method: "GET",
                    headers: {
                        "Content-type": "application/json",
                        Authorization: `Bearer ${access_token}`
                    }
                }
            );
            const data = await response.json()
            window.location.href = data.url; // Redirect to the Google login page

        } catch (error) {
            dispatch(
                setError({
                    msg: `Failed to fetch authorization URL: ${error}`,
                    statusCode: 500,
                })
            );
        }

    };

    async function handleDisconnectGoogleCalendar() {
        setLoading(true);
        try {
            const response = await fetch(`${apiUrl}/google/revoke/`,
                {
                    method: "GET",
                    headers: {
                        "Content-type": "application/json",
                        Authorization: `Bearer ${access_token}`
                    }
                }
            );
            const data = await response.json()
            if (response.ok) {
                setIsGoogleConnected(false)
                dispatch(setSuccess({ msg: data.message, statusCode: 200 }))
            } else {
                dispatch(
                    setError({
                        msg: data.error,
                        statusCode: 400,
                    })
                );
            }

        } catch (error) {
            dispatch(
                setError({
                    msg: `Error disconnecting Google calendar: ${error}`,
                    statusCode: 500,
                })
            );
            // Handle errors here, e.g., show an error message
        }
        setLoading(false);
    };

    async function handleConnectMicrosoftCalendar() {
        setMsLoading(true);
        try {

            const response = await fetch(`${apiUrl}/microsoft/authorize/`,
                {
                    method: "GET",
                    headers: {
                        "Content-type": "application/json",
                        Authorization: `Bearer ${access_token}`
                    }
                }
            );
            const data = await response.json()
            window.location.href = data.url; // Redirect to the Microsoft login page

        } catch (error) {
            dispatch(
                setError({
                    msg: `Failed to fetch authorization URL: ${error}`,
                    statusCode: 500,
                })
            );
        }

    };

    async function handleDisconnectMicrosoftCalendar() {
        setMsLoading(true);
        try {
            const response = await fetch(`${apiUrl}/microsoft/revoke/`,
                {
                    method: "GET",
                    headers: {
                        "Content-type": "application/json",
                        Authorization: `Bearer ${access_token}`
                    }
                }
            );
            const data = await response.json()
            if (response.ok) {
                setIsMicrosoftConnected(false)
                dispatch(setSuccess({ msg: data.message, statusCode: 200 }))
            } else {
                dispatch(
                    setError({
                        msg: data.error,
                        statusCode: 400,
                    })
                );
            }

        } catch (error) {
            dispatch(
                setError({
                    msg: `Error revoking Microsoft Credentials: ${error}`,
                    statusCode: 500,
                })
            );


        }
        setMsLoading(false);
    };

    async function handleDisconnectZoomClient() {
        setZmLoading(true);
        try {
            const response = await fetch(`${apiUrl}/zoom/revoke/`,
                {
                    method: "GET",
                    headers: {
                        "Content-type": "application/json",
                        Authorization: `Bearer ${access_token}`
                    }
                }
            );
            const data = await response.json()
            if (response.ok) {
                setIsZoomConnected(false)
                dispatch(setSuccess({ msg: data.message, statusCode: 200 }))
            } else {
                dispatch(
                    setError({
                        msg: data.error,
                        statusCode: 400,
                    })
                );
            }

        } catch (error) {
            dispatch(
                setError({
                    msg: `Error revoking Zoom Credentials: ${error}`,
                    statusCode: 500,
                })
            );


        }
        setZmLoading(false);
    };

    async function fetchUser() {
        setIsLoading(true);
        try {

            const response = await fetch(`${apiUrl}/user`,
                {
                    method: "GET",
                    headers: {
                        "Content-type": "application/json",
                        Authorization: `Bearer ${access_token}`
                    }
                }
            );
            const data = await response.json()
            setEmail(data.email)
            setName(data.name)
            setLastUpdate(new Date(data.last_update || data.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }))
            setIsGoogleConnected(data.has_google_creds)
            setIsMicrosoftConnected(data.has_microsoft_creds)
            setIsZoomConnected(data.has_zoom_creds)
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            dispatch(
                setError({
                    msg: `Error fetching current user: ${error}`,
                    statusCode: 500,
                })
            );
        }
    }

    useEffect(() => {
        fetchUser();
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('tiller');
        navigate('/signin');
    };

    const handleCancel = (field: string) => {
        if (field === 'name') setIsUpdateName(false);
        if (field === 'email') setIsUpdateEmail(false);
        if (field === 'password') setIsUpdatePassword(false);
    };

    return (
        <DashboardLayout>
            <div className="overflow-auto lg:pr-60 lg:ml-7">

                <div className="flex flex-row  md:block justify-between mb-10 ">
                    <h2 className="font-bold text-[22px] md:text-2xl text-gray-700">Settings</h2>
                    <button className='text-[#ED6459] md:hidden border border-[#ED6459] btn px-2 py-1' onClick={handleLogout}>Log out</button>
                </div>
                {reqStatus && (
                    <AppNotifications {...reqStatus} />
                )}

                {isLoading ? <SettingsSkeletonLoader /> : (

                    <div>
                        <div className="flex flex-col gap-3 bg-white px-6 py-4 rounded-lg mb-4 lg:w-[60vw]">
                            <div className="flex flex-row justify-start items-center mb-6">
                                <p className="font-semibold md:text-lg">Personal details</p>
                            </div>

                            {/* Name Section */}
                            <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center md:gap-0 border-b pb-4">
                                <div className="flex flex-col gap-1">
                                    <p className="text-[#6B7280] text-sm">Name</p>
                                    <div className={`flex flex-col gap-3 transition-all duration-400 overflow-hidden ${isUpdateName ? 'max-h-96' : 'max-h-0 opacity-0'}`}
                                    >
                                        <input type="text" name="name" value={name} className="form-input" onChange={handleNameChange} />
                                        {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
                                        <div className="flex flex-row gap-2">
                                            <button className="btn btn-outline text-xs" onClick={() => handleCancel('name')}>Cancel</button>
                                            <button className="btn btn-aqua text-xs" onClick={() => handleUpdate('name', name)}>Save</button>
                                        </div>
                                    </div>
                                    <div className={`${isUpdateName ? 'hidden' : ''}`} >
                                        {name}
                                    </div>
                                </div>
                                {!isUpdateName && (
                                    <button className="btn btn-outline flex items-center gap-3 max-w-fit" onClick={() => { setIsUpdateName(true) }}>
                                        <Icon icon="streamline:pencil" /> Edit
                                    </button>
                                )}
                            </div>

                            {/* Email Section */}
                            <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center md:gap-0 border-b pb-4">
                                <div className="flex flex-col gap-1">
                                    <p className="text-[#6B7280] text-sm">Email</p>
                                    <div className={`flex flex-col gap-3 transition-all duration-400 overflow-hidden ${isUpdateEmail ? 'max-h-96' : 'max-h-0 opacity-0'} `}
                                    >
                                        <input type="text" name="email" value={email} className="form-input" onChange={handleEmailChange} />
                                        {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
                                        <div className="flex flex-row gap-2">
                                            <button className="btn btn-outline text-xs" onClick={() => handleCancel('email')}>Cancel</button>
                                            <button className="btn btn-aqua text-xs" onClick={() => handleUpdate('email', email)}>Save</button>
                                        </div>
                                    </div>
                                    <div className={`transition-all duration-400 ${isUpdateEmail ? 'hidden' : ''}`}>
                                        {email}
                                    </div>
                                </div>
                                {!isUpdateEmail && (
                                    <button className="btn btn-outline flex items-center gap-3 max-w-fit" onClick={() => { setIsUpdateEmail(true) }}>
                                        <Icon icon="streamline:pencil" /> Edit
                                    </button>
                                )}
                            </div>

                            {/* Password Section */}
                            <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center md:gap-0 pb-1">
                                <div className="flex flex-col gap-1">
                                    <p className="text-[#6B7280] text-sm">Password</p>
                                    <div className={`flex flex-col gap-3 transition-all duration-400 overflow-hidden ${isUpdatePassword ? 'max-h-96' : 'max-h-0 opacity-0'}`}
                                    >
                                        <div className="relative">
                                            <label htmlFor="old-pwd" className="block w-full">Old Password</label>
                                            <input type={oldPwdVisible ? "text" : "password"} value={oldPwd} onChange={handleOldPwdChange} id="old-pwd" className="form-input pr-10" required />
                                            <button
                                                type="button"
                                                onClick={() => setOldPwdVisible(!oldPwdVisible)}
                                                className="absolute inset-y-0 right-0 pr-3 mt-6 flex items-center text-sm leading-5"
                                            >
                                                {oldPwdVisible ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                        {errors.oldPwd && <p className="text-red-500 text-xs">{errors.oldPwd}</p>}
                                        {errors.oldPwdDetails && <p className="text-red-500 text-xs">{errors.oldPwdDetails}</p>}
                                        <div className="relative">
                                            <label htmlFor="new-pwd" className="block w-full">New Password</label>
                                            <input type={newPwdVisible ? "text" : "password"} value={newPwd} onChange={handleNewPwdChange} id="new-pwd" className="form-input pr-10" required />
                                            <button
                                                type="button"
                                                onClick={() => setNewPwdVisible(!newPwdVisible)}
                                                className="absolute inset-y-0 right-0 pr-3 mt-6 flex items-center text-sm leading-5"
                                            >
                                                {newPwdVisible ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                        {errors.newPwd && <p className="text-red-500 text-xs">{errors.newPwd}</p>}
                                        {errors.newPwdDetails && <p className="text-red-500 text-xs">{errors.newPwdDetails}</p>}
                                        <div className="flex flex-row gap-2">
                                            <button className="btn btn-outline text-xs" onClick={() => handleCancel('password')}>Cancel</button>
                                            <button className="btn btn-aqua text-xs" onClick={handlePasswordUpdate}>Save</button>
                                        </div>
                                    </div>
                                    <div className={`transition-all duration-400 ${isUpdatePassword ? 'hidden' : ''}`}>
                                        •••••••••••
                                    </div>
                                </div>
                                {!isUpdatePassword && (
                                    <button className="btn btn-outline max-w-fit" onClick={() => { setIsUpdatePassword(true) }}>
                                        Change Password
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Apps */}
                        <div className="flex flex-col gap-4 bg-white px-6 py-4 rounded-lg mb-4 lg:w-[60vw]">
                            <div className="flex flex-row justify-between items-center">
                                <div className="font-semibold md:text-lg">Apps</div>
                                <div></div>
                            </div>
                            <div className="flex flex-row justify-between items-center border-b pb-4">
                                <div className="flex flex-row gap-4 text-[#6B7280] text-sm md:text-base">
                                    <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer" title="Open Google Calendar">
                                        <Icon icon="logos:google-calendar" className="h-6 w-6" />
                                    </a>
                                    Google Calendar</div>
                                {isGoogleConnected ?
                                    <button className="btn btn-outline flex items-center justify-center" onClick={handleDisconnectGoogleCalendar}>
                                        Disconnect
                                        {loading && (
                                            <FaSpinner className="ml-2 inline animate-spin" />
                                        )}
                                    </button> :

                                    <button className="btn btn-outline flex items-center justify-center " onClick={handleConnectGoogleCalendar}>
                                        Connect
                                        {loading && (
                                            <FaSpinner className="ml-2 inline animate-spin" />
                                        )}
                                    </button>
                                }
                            </div>

                            <div className="flex flex-row justify-between items-center border-b pb-4">
                                <div className="flex flex-row gap-4 text-[#6B7280] text-sm md:text-base">
                                    <a href="https://outlook.office365.com/calendar" target="_blank" rel="noopener noreferrer" title="Open Microsoft Calendar">
                                        <Icon icon="logos:microsoft-teams" className="h-6 w-6" />
                                    </a>
                                    Microsoft Teams</div>
                                {isMicrosoftConnected ?
                                    <button className="btn btn-outline flex items-center justify-center" onClick={handleDisconnectMicrosoftCalendar}>
                                        Disconnect
                                        {msLoading && (
                                            <FaSpinner className="ml-2 inline animate-spin" />
                                        )}
                                    </button> :

                                    <button className="btn btn-outline flex items-center justify-center" onClick={handleConnectMicrosoftCalendar}>
                                        Connect
                                        {msLoading && (
                                            <FaSpinner className="ml-2 inline animate-spin" />
                                        )}
                                    </button>
                                }
                            </div>
                            <div className="flex flex-row justify-between items-center pb-2">
                                <div className="flex flex-row gap-4 text-[#6B7280] text-sm md:text-base">
                                    <a href="https://us02web.zoom.us/meeting#/upcoming" target="_blank" rel="noopener noreferrer" title="Open Zoom Client">
                                        <Icon icon="akar-icons:zoom-fill" color="#4A8CFF" className="h-6 w-6" />
                                    </a>

                                    Zoom</div>
                                {isZoomConnected ?
                                    <button className="btn btn-outline flex items-center justify-center" onClick={handleDisconnectZoomClient}>
                                        Disconnect
                                        {zmLoading && (
                                            <FaSpinner className="ml-2 inline animate-spin" />
                                        )}
                                    </button> :
                                    <div className="relative group inline-block border-opacity-50 bg-opacity-50">
                                        <button className="btn btn-outline flex items-center justify-center" onClick={handleConnectZoomClient} >
                                            Connect
                                            {zmLoading && (
                                                <FaSpinner className="ml-2 inline animate-spin" />
                                            )}
                                        </button>
                                    </div>
                                }
                            </div>
                        </div>
                        <div className="flex flex-col gap-3 bg-white px-6 py-4 rounded-lg mb-4 lg:w-[60vw]">
                            <div className="flex flex-row justify-start items-center mb-6">
                                <p className="font-semibold md:text-lg">Company management</p>
                            </div>
                            <div className="flex flex-row justify-between text-[#6B7280] text-sm md:text-base cursor-pointer" onClick={() => navigate('/settings/teams-departments')}>
                                <div>Teams & Departments</div>
                                <div><Icon icon="fluent:arrow-right-28-regular" className="h-6 w-6" /></div>
                            </div>

                        </div>
                    </div>)}
            </div>
        </DashboardLayout>
    )

}

export default DashboardSettings;
